import { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { alertActions } from '../redux/AlertController';

const API = `${process.env.REACT_APP_API_BASE_URL}/api/admin/assistant`;

// Users' smart assistant management: settings, common questions, conversations, usage & cost.
export const useUserAssistant = () => {
    const user = useSelector((state) => state.userController.user);
    const dispatch = useDispatch();
    const [config, setConfig] = useState(null);
    const [meta, setMeta] = useState(null); // model, prices, defaults, apiKeyConfigured
    const [faqs, setFaqs] = useState([]);

    const request = useCallback(async (path, { method = 'GET', body } = {}) => {
        const response = await fetch(`${API}${path}`, {
            method,
            headers: {
                Authorization: `Bearer ${user?.token}`,
                ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
            },
            ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
        });
        const json = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(json.error || 'حدث خطأ غير متوقع');
        return json;
    }, [user?.token]);

    const notify = useCallback((msg, type = 'success') => dispatch(alertActions.showAlert({ msg, type })), [dispatch]);

    const safe = useCallback(async (promise, successMsg) => {
        try {
            const json = await promise;
            if (successMsg) notify(successMsg);
            return json;
        } catch (error) {
            notify(error.message, 'error');
            return null;
        }
    }, [notify]);

    const loadConfig = useCallback(async () => {
        const json = await safe(request('/config'));
        if (json) {
            setConfig(json.config);
            setMeta({ model: json.model, pricePerMillion: json.pricePerMillion, defaults: json.defaults, apiKeyConfigured: json.apiKeyConfigured });
        }
    }, [request, safe]);

    const saveConfig = async (payload) => {
        const json = await safe(request('/config', { method: 'PUT', body: payload }), 'تم حفظ إعدادات المساعد');
        if (json) setConfig(json.config);
        return Boolean(json);
    };

    const loadFaqs = useCallback(async () => {
        const json = await safe(request('/faqs'));
        if (json) setFaqs(json.faqs || []);
    }, [request, safe]);

    const saveFaq = async (faq) => {
        const json = faq._id
            ? await safe(request(`/faqs/${faq._id}`, { method: 'PUT', body: faq }), 'تم حفظ السؤال')
            : await safe(request('/faqs', { method: 'POST', body: faq }), 'تمت إضافة السؤال');
        if (json) await loadFaqs();
        return Boolean(json);
    };

    const deleteFaq = async (id) => {
        const json = await safe(request(`/faqs/${id}`, { method: 'DELETE' }), 'تم حذف السؤال');
        if (json) await loadFaqs();
    };

    const reorderFaqs = async (ids) => {
        setFaqs((prev) => ids.map((id) => prev.find((f) => f._id === id)).filter(Boolean));
        await safe(request('/faqs/reorder', { method: 'POST', body: { ids } }));
    };

    const listConversations = (params = {}) => {
        const qs = new URLSearchParams(Object.fromEntries(Object.entries(params).filter(([, v]) => v)));
        return safe(request(`/conversations?${qs}`));
    };
    const getConversation = (id) => safe(request(`/conversations/${id}`));
    const listUnanswered = () => safe(request('/unanswered'));
    const getStats = (days = 30) => safe(request(`/stats?days=${days}`));

    return { config, meta, faqs, loadConfig, saveConfig, loadFaqs, saveFaq, deleteFaq, reorderFaqs, listConversations, getConversation, listUnanswered, getStats };
};
